import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Sparkles, Download, Loader2, Check, X, Image as ImageIcon, Plus, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Field, Input, Textarea } from "@/components/FormBits";
import { api, API, fileUrl, pdfUrl, setToken } from "@/lib/api";

/** Editable fields used by the visual Case Study PDF.  Backend reads these from
 *  project.case_study_data and uses them to override AI / project values. */
const FIELDS = [
  { key: "title", label: "Project title (large headline)", placeholder: "Braunstone Culvert Replacement" },
  { key: "category_pill", label: "Category pill (top-left)", placeholder: "Culvert Replacement & Flood Mitigation" },
  { key: "intro", label: "Short intro (3–4 lines)", textarea: true, placeholder: "e.g. The company replaced a failing culvert crossing…" },
  { key: "client", label: "Client", placeholder: "Leicester City Council" },
  { key: "location_text", label: "Location", placeholder: "Mossdale Meadows, Braunstone, Leicester" },
  { key: "year", label: "Year", placeholder: "2024" },
  { key: "challenge", label: "The Challenge (1 short paragraph)", textarea: true },
  { key: "solution", label: "The Solution (1 short paragraph)", textarea: true },
  { key: "outcome", label: "The Outcome (1 short paragraph)", textarea: true },
];

export default function AdminCaseStudyEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [data, setData] = useState({});
  const [keyFacts, setKeyFacts] = useState([]);
  const [heroPath, setHeroPath] = useState("");
  const [galleryPaths, setGalleryPaths] = useState([]);
  const [busy, setBusy] = useState("");
  const [titleIdeas, setTitleIdeas] = useState([]);
  const [showTitleIdeas, setShowTitleIdeas] = useState(false);

  const load = async () => {
    const { data: p } = await api.get(`/admin/projects/${id}`);
    setProject(p);
    const csd = p.case_study_data || {};
    // pre-fill missing values from AI draft / project fields so the editor is always populated
    const ai = p.case_study_draft || {};
    setData({
      title: csd.title ?? ai.suggested_title ?? p.short_title ?? p.project_name ?? "",
      category_pill: csd.category_pill ?? (p.categories || []).slice(0, 2).join(" & "),
      intro: csd.intro ?? ai.short_intro ?? p.one_sentence ?? "",
      client: csd.client ?? p.client_name ?? "",
      location_text: csd.location_text ?? p.location ?? "",
      year: csd.year ?? p.year_completed ?? "",
      challenge: csd.challenge ?? ai.challenge ?? p.main_challenge_text ?? "",
      solution: csd.solution ?? ai.solution ?? p.works_carried_out ?? "",
      outcome: csd.outcome ?? ai.outcome ?? p.main_result ?? "",
    });
    // key facts
    let kf = csd.key_facts;
    if (!kf) {
      const aiKf = ai.key_facts;
      if (Array.isArray(aiKf)) kf = aiKf;
      else if (typeof aiKf === "string" && aiKf) kf = aiKf.split("\n").filter(Boolean);
      else {
        kf = [];
        for (const [k, lbl] of [["duration", "Duration"], ["length", "Length"], ["area", "Area"], ["tonnes", "Tonnes of material"], ["operatives", "Operatives"], ["plant_used", "Plant used"]]) {
          if (p[k]) { kf.push(`${p[k]}${lbl !== p[k] ? "" : ""}`); }
          if (kf.length >= 5) break;
        }
      }
    }
    setKeyFacts((kf || []).slice(0, 5));
    setHeroPath(p.case_study_hero_path || "");
    setGalleryPaths(p.case_study_gallery_paths || []);
  };

  useEffect(() => { load(); }, [id]);

  const images = useMemo(
    () => (project?.files || []).filter((f) => !f.is_deleted && (f.content_type || "").startsWith("image/")),
    [project]
  );

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setKf = (i, v) => setKeyFacts((arr) => arr.map((x, idx) => (idx === i ? v : x)));
  const addKf = () => keyFacts.length < 5 && setKeyFacts([...keyFacts, ""]);
  const delKf = (i) => setKeyFacts(keyFacts.filter((_, idx) => idx !== i));

  const toggleGallery = (path) => {
    setGalleryPaths((g) => {
      if (g.includes(path)) return g.filter((x) => x !== path);
      if (g.length >= 3) {
        toast.error("Gallery is limited to 3 images");
        return g;
      }
      return [...g, path];
    });
  };

  const save = async () => {
    setBusy("save");
    try {
      await api.patch(`/admin/projects/${id}/case-study-data`, {
        data: { ...data, key_facts: keyFacts.filter(Boolean) },
        hero_path: heroPath || null,
        gallery_paths: galleryPaths,
      });
      toast.success("Case study saved");
    } catch (e) {
      toast.error("Save failed");
    } finally { setBusy(""); }
  };

  const generateTitleIdeas = async () => {
    setBusy("titles");
    try {
      const { data } = await api.post(`/admin/projects/${id}/generate-titles`);
      setTitleIdeas(data.titles || []);
      setShowTitleIdeas(true);
    } catch (e) {
      toast.error("Couldn't generate titles");
    } finally { setBusy(""); }
  };


  const runAi = async () => {
    setBusy("ai");
    try {
      const { data: ai } = await api.post(`/admin/projects/${id}/generate-case-study`);
      // overwrite only empty fields so we never blow away admin edits
      setData((d) => ({
        title: d.title || ai.suggested_title || d.title,
        category_pill: d.category_pill,
        intro: d.intro || ai.short_intro || d.intro,
        client: d.client,
        location_text: d.location_text,
        year: d.year,
        challenge: d.challenge || ai.challenge || d.challenge,
        solution: d.solution || ai.solution || d.solution,
        outcome: d.outcome || ai.outcome || d.outcome,
      }));
      toast.success("AI draft generated — review and tweak");
    } catch (e) {
      toast.error("AI draft failed");
    } finally { setBusy(""); }
  };

  const downloadPdf = async () => {
    setBusy("pdf");
    try {
      await save();
      const { data } = await api.post(`/admin/projects/${id}/email-case-study`);
      const to = (data?.to || []).join(", ");
      toast.success(`Case study emailed to ${to}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not email case study");
    }
    finally { setBusy(""); }
  };

  if (!project) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-20 text-center text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10" data-testid="case-study-editor">
        <Link to={`/admin/projects/${id}`} className="rcw-btn-ghost text-sm mb-4">
          <ArrowLeft className="h-4 w-4" /> Back to project
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div>
            <div className="rcw-label">Case study editor</div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-gray-900 mt-1">
              {project.project_name || "Project"}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Edit the visual PDF content. Pick a hero image and 2–3 gallery shots, then download.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={runAi} disabled={busy !== ""} className="rcw-btn-ghost text-sm" data-testid="cs-ai-fill">
              {busy === "ai" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} AI fill empty
            </button>
            <button onClick={save} disabled={busy !== ""} className="rcw-btn-ghost text-sm" data-testid="cs-save">
              {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
            </button>
            <button onClick={downloadPdf} disabled={busy !== ""} className="rcw-btn-amber text-sm" data-testid="cs-download">
              {busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Save & email PDF
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 rcw-card p-5 sm:p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="rcw-label">Headline</div>
              <button
                onClick={generateTitleIdeas}
                disabled={busy !== ""}
                className="rcw-btn-ghost text-xs"
                data-testid="cs-title-ideas"
              >
                {busy === "titles" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
                Title ideas
              </button>
            </div>
            {FIELDS.slice(0, 6).map((f) => (
              <Field key={f.key} label={f.label}>
                {f.textarea ? (
                  <Textarea value={data[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)} placeholder={f.placeholder} data-testid={`cs-${f.key}`} />
                ) : (
                  <Input value={data[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)} placeholder={f.placeholder} data-testid={`cs-${f.key}`} />
                )}
              </Field>
            ))}
            <div className="border-t border-gray-100 pt-4">
              <div className="rcw-label">Body</div>
              {FIELDS.slice(6).map((f) => (
                <Field key={f.key} label={f.label}>
                  <Textarea value={data[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)} placeholder={f.placeholder} data-testid={`cs-${f.key}`} />
                </Field>
              ))}
            </div>
          </div>

          <div className="space-y-5">
            {/* Key facts */}
            <div className="rcw-card p-5 sm:p-6">
              <div className="rcw-label mb-3">Key facts (up to 5)</div>
              <div className="space-y-2">
                {keyFacts.map((kf, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input value={kf} onChange={(e) => setKf(i, e.target.value)} placeholder="e.g. 60 t of concrete" data-testid={`cs-kf-${i}`} />
                    <button onClick={() => delKf(i)} className="text-gray-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                {keyFacts.length < 5 && (
                  <button onClick={addKf} className="rcw-btn-ghost text-xs mt-2" data-testid="cs-kf-add">
                    <Plus className="h-3.5 w-3.5" /> Add fact
                  </button>
                )}
              </div>
            </div>

            {/* PDF image ticks */}
            <div className="rcw-card p-5 sm:p-6">
              <div className="flex items-center justify-between mb-1">
                <div className="rcw-label">PDF images</div>
                <div className="text-xs text-gray-500">{galleryPaths.length} / 3 ticked</div>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Tick the images to use in the PDF (up to 3 for the gallery). Click{" "}
                <span className="font-semibold text-amber-600">Hero</span> on one image to make it the full-width cover photo.
              </p>
              {images.length === 0 ? (
                <div className="text-sm text-gray-500">Upload images on the project page first.</div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {images.map((f, i) => {
                    const isHero = heroPath === f.storage_path;
                    const idx = galleryPaths.indexOf(f.storage_path);
                    const ticked = idx >= 0;
                    const tid = f.id || i;
                    return (
                      <div
                        key={f.storage_path}
                        className={`relative aspect-video rounded-lg overflow-hidden border-2 transition ${
                          isHero ? "border-amber-500 ring-2 ring-amber-300" : ticked ? "border-emerald-500 ring-2 ring-emerald-200" : "border-transparent hover:border-gray-300"
                        }`}
                        data-testid={`cs-pdf-img-${tid}`}
                      >
                        <img src={fileUrl(f.storage_path)} alt={f.original_filename} className="w-full h-full object-cover" />
                        {/* Tick — use in PDF gallery */}
                        <button
                          onClick={() => toggleGallery(f.storage_path)}
                          title={ticked ? "Remove from PDF" : "Use this image in the PDF"}
                          className={`absolute top-1.5 left-1.5 h-7 w-7 rounded-md inline-flex items-center justify-center border-2 shadow-sm transition ${
                            ticked ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white/90 border-gray-300 text-transparent hover:border-emerald-400"
                          }`}
                          data-testid={`cs-pdf-img-tick-${tid}`}
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        {ticked && (
                          <div className="absolute top-1.5 left-10 h-7 px-2 rounded-md bg-emerald-500/90 text-white text-[10px] font-bold inline-flex items-center">
                            #{idx + 1} in PDF
                          </div>
                        )}
                        {/* Hero selector */}
                        <button
                          onClick={() => setHeroPath(isHero ? "" : f.storage_path)}
                          className={`absolute bottom-1.5 right-1.5 rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-wide shadow-sm transition ${
                            isHero ? "bg-amber-500 text-white" : "bg-white/90 text-gray-600 hover:bg-amber-100"
                          }`}
                          data-testid={`cs-pdf-img-hero-${tid}`}
                        >
                          {isHero ? "★ Hero" : "Hero"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {showTitleIdeas && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowTitleIdeas(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-xl w-full p-6 sm:p-7" onClick={(e) => e.stopPropagation()} data-testid="title-ideas-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="rcw-label">Suggested titles</div>
              <button onClick={() => setShowTitleIdeas(false)} className="text-gray-400 hover:text-gray-700"><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Click a title to use it — it'll replace the headline above. AI generated from your project data, no facts invented.
            </p>
            <div className="space-y-2 max-h-[55vh] overflow-y-auto">
              {titleIdeas.length === 0 && (
                <div className="text-sm text-gray-500">No titles generated.</div>
              )}
              {titleIdeas.map((t, i) => (
                <button
                  key={i}
                  onClick={() => { setField("title", t); setShowTitleIdeas(false); toast.success("Title updated"); }}
                  className="w-full text-left rounded-lg border border-gray-200 hover:border-amber-300 hover:bg-amber-50/40 px-3 py-2.5 text-sm text-gray-900 transition"
                  data-testid={`title-idea-${i}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
